import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { FaLayerGroup, FaServer } from 'react-icons/fa';
import axiosInstance from '../utility/axiosInstance';
import { getActionSelectStyles } from '../utility/selectStyles';
import ConfirmationModal from './ConfirmationModal';
import '../styles/RegistrationBilling.scss';

type PlanId = 'free' | 'standard' | 'standard_plus';
type Notice = { id: string; message: string; url: string; dismissible: boolean };
interface Account {
    enrolled: boolean; available_plans?:PlanId[]; plan: PlanId; pending_plan: PlanId | null; generation: string; accounting_status: string;
    plan_change?: {state:'pending'|'review';requested_plan:PlanId|null;target_plan:PlanId|null;effective_at:string|null;last_error:string|null}|null;
    commitment_gbp: number; commitment_credits_gbp?: number; credits_remaining: number; period_end: string | null;
    free_entitlement: boolean; credits: { id: string; category: string; remaining: number; expires_at: string }[];
    free_resources: { kind: string; state: string; resource_id: string | null }[];
    free_egress?: {used_bytes:string;remaining_bytes:string;allowance_bytes:string;resets_at:string;paused:boolean};
    free_lifecycle?: {suspended_at:string|null;delete_after:string|null};
    activated_at?:string|null;
    included_credits?:{total:number;used:number;percentage:number};
    notices: Notice[];
    budget: { limit: number | null; spent: number; percentage: number; reached: boolean; zero_mode: string; minimum:number; paid_usage:number; subscription_fee:number; deletion_confirmed:boolean };
}
/** What the budget card needs from the plan: its lowest budget (the commitment) and whether a card is on the account. */
export interface PlanBudgetContext { minimum: number; cardless: boolean }

// The plan names of the signup page (Milos, 2026-11-01: "Free should be regular").
const names: Record<PlanId, string> = { free: 'Regular', standard: 'Professional', standard_plus: 'Enterprise' };
const money = (value: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
const date = (value: string) => new Date(value).toLocaleDateString('en-GB', { dateStyle: 'medium' });
// The free tier as the plans page lists it (Milos, 2026-11-01: never a "website VM").
const FREE_TIER: Record<string, { name: string; spec: string }> = {
    app: { name: 'VM', spec: '1 vCPU, 2 GB RAM, 10 GB storage' },
    website: { name: 'VM', spec: '1 vCPU, 2 GB RAM, 10 GB storage' },
    postgres: { name: 'Private PostgreSQL', spec: '1 vCPU, 1 GB RAM, 2 GB storage' },
    database: { name: 'Private PostgreSQL', spec: '1 vCPU, 1 GB RAM, 2 GB storage' },
};
const stateLabel = (state: string) => state ? state.charAt(0).toUpperCase() + state.slice(1).replace(/_/g, ' ') : '';
// Each paid monthly commitment comes back as usage credits for its own billing period ('purchased' grants).
const creditLabel = (category: string) => category === 'purchased' ? 'Commitment' : stateLabel(category);
const creditsBack = (commitment: number, credits?: number) =>
    `comes back as ${money(credits ?? commitment)} of usage credits each billing period`;
const gb = (bytes: string | number) => (Number(bytes) / 1_000_000_000).toFixed(2);

/**
 * A plan account's billing: the plan, the monthly safety budget (the console's own budget card, passed in as renderBudget),
 * the free tier, and the plan change. Existing accounts keep their original billing UI (children): no automatic enrollment.
 */
export default function RegistrationBilling({ children, renderBudget }: {
    children: React.ReactNode; renderBudget?: (plan: PlanBudgetContext) => React.ReactNode;
}) {
    const [account, setAccount] = useState<Account | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [plan, setPlan] = useState<PlanId>('free');
    const [planStatusUnknown,setPlanStatusUnknown] = useState(false);
    const [confirmation, setConfirmation] = useState<{ title: string; message: string; run: () => Promise<void>; kind?:'plan' } | null>(null);
    async function load() {
        const { data } = await axiosInstance.get<Account>('/v1/billing/account');
        setAccount(data.enrolled ? data : null);
        setPlanStatusUnknown(false);
        if(data.enrolled && data.plan_change)setConfirmation(current=>current?.kind==='plan'?null:current);
        if (data.enrolled) setPlan(data.plan);
        setLoaded(true);
    }
    useEffect(() => {
        const reload = () => { load().catch(() => { setError('Could not load account billing. Reload to try again.'); }); };
        reload(); window.addEventListener('registration-account-changed',reload);
        return () => window.removeEventListener('registration-account-changed',reload);
    }, []);
    async function perform(action: () => Promise<void | boolean>) {
        setBusy(true); setError('');
        try { const applied = await action(); if (applied !== false) { await load(); setConfirmation(null); window.dispatchEvent(new Event('registration-account-changed')); } }
        catch (e: any) { setConfirmation(null); setError(e.response?.data?.error?.message || (confirmation?.kind==='plan'
            ? 'Your plan request is not confirmed yet. Check its status below before trying another change.'
            : 'Could not save. Try again.')); }
        finally { setBusy(false); }
    }
    async function previewPlan(value: PlanId | null) {
        if(account?.plan_change || planStatusUnknown)return false;
        const { data } = await axiosInstance.post('/v1/billing/plan-change/preview', { plan: value });
        setConfirmation({ kind:'plan',title: value ? `Change to ${names[value]} at renewal` : 'Cancel the scheduled change',
            message: value ? `${names[value]} starts ${data.effective_at ? date(data.effective_at) : 'at renewal'} ${data.commitment_gbp > 0
                ? `with a ${money(data.commitment_gbp)} monthly commitment, excluding taxes. It ${creditsBack(data.commitment_gbp, data.commitment_credits_gbp)}.`
                : 'with no monthly commitment.'} ${data.warning || ''} Your free tier stays.` : 'Your current plan continues at renewal.',
            run: async () => {
                // A failed response may follow a saved intent. Refresh its durable status before
                // allowing another plan mutation; never turn a provider timeout into apparent success.
                try { await axiosInstance.put('/v1/billing/plan-change', { plan: value, generation: data.generation, confirmed: true }); }
                catch(error) {
                    try { await load(); } catch { setPlanStatusUnknown(true); }
                    throw error;
                }
                try { await load(); } catch(error) { setPlanStatusUnknown(true);throw error; }
            } });
        return false;
    }
    if (!loaded) return <p role={error ? 'alert' : 'status'}>{error || 'Loading account billing...'}</p>;
    if (!account) return <>{children}</>;
    const cardless = !account.activated_at;
    const planOptions = (Object.entries(names) as [PlanId, string][])
        .filter(([id]) => !account.available_plans || account.available_plans.includes(id))
        .map(([value, label]) => ({ value, label }));
    const planLocked = busy || !!account.plan_change || planStatusUnknown;
    const egress = account.free_egress;
    const egressUsed = egress ? Math.max(0, Math.min(100, (Number(egress.used_bytes) / Math.max(1, Number(egress.allowance_bytes))) * 100)) : 0;
    return <section className="registration-billing" aria-label="Plan and spending">
        <div className="registration-billing__card">
            <div className="registration-billing__heading">
                <span className="registration-billing__icon"><FaLayerGroup /></span>
                <div><h3>{names[account.plan]}</h3></div>
                <span className="registration-billing__pill">{cardless ? 'No card' : 'Active'}</span>
            </div>
            {error && <p role="alert" className="registration-billing__error">{error}</p>}
            {/* The add-card and set-budget notices are said once: by the top banner, and for the budget by its card below. */}
            {account.notices.filter(notice => !['add_card', 'set_budget'].includes(notice.id)).map(notice => <aside key={notice.id} className="registration-billing__notice">
                <p>{notice.message} <a href={notice.url}>Review account</a></p>
                {notice.dismissible && <button className="billing-secondary-action" disabled={busy} onClick={() => void perform(async () => {
                    await axiosInstance.post('/v1/billing/notices/dismiss', { id: notice.id });
                })}>Dismiss</button>}
            </aside>)}
            <div className="registration-billing__stats">
                <div><span>Monthly commitment</span><strong>{money(account.commitment_gbp)}</strong></div>
                <div><span>Credits</span><strong>{money(account.credits_remaining)}</strong></div>
                <div><span>Next period</span><strong>{account.period_end ? date(account.period_end) : 'Pending'}</strong></div>
            </div>
            {account.commitment_gbp > 0 && <p className="registration-billing__note">
                Your {money(account.commitment_gbp)} monthly commitment {creditsBack(account.commitment_gbp, account.commitment_credits_gbp)}.</p>}
            {account.credits.length > 0 && <ul className="registration-billing__credits" aria-label="Credit grants">{account.credits.map(credit => <li key={credit.id}>
                <span>{creditLabel(credit.category)}</span><strong>{money(credit.remaining)}</strong><small>expires {date(credit.expires_at)}</small>
            </li>)}</ul>}
        </div>

        {renderBudget?.({ minimum: account.budget.minimum, cardless })}

        <div className="registration-billing__card">
            <div className="registration-billing__heading">
                <span className="registration-billing__icon"><FaServer /></span>
                <div><h3>Free tier included</h3></div>
            </div>
            <ul className="registration-billing__free">
                {(account.free_resources.length ? account.free_resources : [{ kind: 'app', state: '', resource_id: null }, { kind: 'postgres', state: '', resource_id: null }])
                    .map(resource => { const tier = FREE_TIER[resource.kind] || { name: stateLabel(resource.kind), spec: '' };
                        return <li key={resource.kind}><strong>{tier.name}</strong><span>{tier.spec}</span>{resource.state && <em>{stateLabel(resource.state)}</em>}</li>; })}
                {egress && <li className="registration-billing__egress">
                    <strong>Egress</strong>
                    <span>{gb(egress.remaining_bytes)} GB of {Math.round(Number(egress.allowance_bytes) / 1_000_000_000)} GB left, resets {date(egress.resets_at)}</span>
                    <div className="registration-billing__meter" aria-hidden="true"><span style={{ width: `${egressUsed}%` }} /></div>
                </li>}
            </ul>
            {egress?.paused && (account.free_lifecycle?.suspended_at
                ? <p role="status" className="registration-billing__note">Add a card to recover your free resources before they are deleted.</p>
                : <p role="status" className="registration-billing__note">Public traffic is paused until the monthly reset. Disks, your database and private networking are kept.</p>)}
            <p className="registration-billing__note">Your agent creates and manages these through MCP.</p>
        </div>

        <div className="registration-billing__card">
            <div className="registration-billing__heading">
                <div><h3>Change plan</h3></div>
            </div>
            {account.plan_change && <aside className="registration-billing__notice" role="status" aria-label="Plan change status">
                <p>{account.plan_change.requested_plan===null ? 'Your cancellation request' : `Your change to ${names[account.plan_change.requested_plan]}`} {account.plan_change.state==='review'
                    ? 'needs billing review. It is not confirmed yet.' : 'is saved and syncing with billing. It is not confirmed yet; we retry automatically.'}</p>
                <p>Requested renewal plan: {names[account.plan_change.target_plan??account.plan]}{account.plan_change.effective_at ? ` from ${date(account.plan_change.effective_at)}` : ''}. Another plan change is unavailable until this request is resolved.</p>
                {account.plan_change.state==='review' && <p><a href="mailto:office@redu.cloud?subject=Plan%20change%20review">Contact support about this plan change</a>{account.plan_change.last_error && <>. Reference: {account.plan_change.last_error}</>}</p>}
                <button className="billing-secondary-action" disabled={busy} onClick={()=>void perform(load)}>Refresh plan status</button>
            </aside>}
            {planStatusUnknown && !account.plan_change && <aside className="registration-billing__notice" role="status">
                <p>The result of your plan request could not be checked. It may already be saved. Refresh its status before making another change.</p>
                <button className="billing-secondary-action" disabled={busy} onClick={()=>void perform(load)}>Refresh plan status</button>
            </aside>}
            {!account.plan_change && !planStatusUnknown && account.pending_plan && <p className="registration-billing__note">
                Scheduled: {names[account.pending_plan]}. <button className="billing-secondary-action" disabled={busy} onClick={() => void perform(() => previewPlan(null))}>Cancel scheduled change</button></p>}
            <div className="registration-billing__change">
                <div className="registration-billing__select">
                    <Select
                        inputId="next-plan"
                        aria-label="Next plan"
                        options={planOptions}
                        value={planOptions.find(option => option.value === plan) || null}
                        onChange={option => { if (option) setPlan(option.value); }}
                        isDisabled={planLocked}
                        isSearchable={false}
                        styles={getActionSelectStyles()}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
                    />
                </div>
                <button className="billing-primary-action" disabled={planLocked || plan === account.plan} onClick={() => void perform(() => previewPlan(plan))}>Change plan</button>
            </div>
            <p className="registration-billing__note">Changes apply at renewal.</p>
        </div>

        <ConfirmationModal isOpen={!!confirmation} title={confirmation?.title || ''} message={confirmation?.message || ''}
            onClose={() => setConfirmation(null)} onConfirm={() => {
                if(confirmation?.kind==='plan' && (account.plan_change || planStatusUnknown)){setConfirmation(null);return;}
                if (confirmation) void perform(confirmation.run);
            }}
            isConfirming={busy} confirmText="Confirm change" type="warning" />
    </section>;
}
